import * as Sentry from '@sentry/browser'
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useGetPartnersEmbeddedOnboardingSessionStatus } from 'utils/web_api'

import { EMBEDDED_ONBOARDING_BEARER_STORAGE_KEY, getEmbeddedOnboardingToken } from './embeddedOnboardingStorage'

interface EmbeddedOnboardingContextType {
  embeddedOnboardingToken: string | null
  isEmbedded: boolean
  setEmbeddedOnboardingToken: (token: string | null) => void
}

const EmbeddedOnboardingContext = createContext<EmbeddedOnboardingContextType>({
  embeddedOnboardingToken: null,
  isEmbedded: false,
  setEmbeddedOnboardingToken: () => {},
})

export function useEmbeddedOnboardingContext () {
  return useContext(EmbeddedOnboardingContext)
}

export function EmbeddedOnboardingProvider ({ children, serverToken }: { children: React.ReactNode; serverToken?: string | null }) {
  // Prefer the server-provided initial bearer first render; fallback to any stored token
  const [embeddedOnboardingToken, setToken] = useState<string | null>(serverToken ?? getEmbeddedOnboardingToken())
  const sessionStatusQuery = useGetPartnersEmbeddedOnboardingSessionStatus({
    query: {
      enabled: !!embeddedOnboardingToken,
    },
  })
  const lastIdentifiedTokenRef = useRef<string | null>(null)

  // Ingest initial bearer from props (e.g., server-provided) and clean URL param once
  useEffect(() => {
    if (serverToken && serverToken !== getEmbeddedOnboardingToken()) {
      setEmbeddedOnboardingToken(serverToken)
    }
    // Remove onboarding client secret from URL once ingested/seen
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.has('onboarding_session_client_secret')) {
        url.searchParams.delete('onboarding_session_client_secret')
        window.history.replaceState({}, '', url.toString())
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }, [serverToken])

  // Identify user once per token when data becomes available
  useEffect(() => {
    if (!embeddedOnboardingToken) return
    if (lastIdentifiedTokenRef.current === embeddedOnboardingToken) return

    const data = sessionStatusQuery.data
    if (!data?.user_id || !data?.email) return

    try {
      window.segment?.identify(data.user_id, { email: data.email })
      window.posthog?.identify(data.user_id, { role: 'user', email: data.email })
      lastIdentifiedTokenRef.current = embeddedOnboardingToken
    } catch {
      // Ignore analytics client failures
    }
  }, [embeddedOnboardingToken, sessionStatusQuery.data])

  const setEmbeddedOnboardingToken = (token: string | null): void => {
    try {
      if (token) {
        window.sessionStorage.setItem(EMBEDDED_ONBOARDING_BEARER_STORAGE_KEY, token)
      } else {
        window.sessionStorage.removeItem(EMBEDDED_ONBOARDING_BEARER_STORAGE_KEY)
      }
      setToken(token)
    } catch (error: unknown) {
      Sentry.captureException(error)
    }
  }

  const value = useMemo<EmbeddedOnboardingContextType>(() => ({
    embeddedOnboardingToken,
    isEmbedded: !!embeddedOnboardingToken,
    setEmbeddedOnboardingToken,
  }), [embeddedOnboardingToken])

  return (
    <EmbeddedOnboardingContext.Provider value={value}>
      {children}
    </EmbeddedOnboardingContext.Provider>
  )
}
